import { getAuthToken, clearAuth } from "./auth";

const BASE_URL = "/api";
type ParsedResponseBody = Record<string, unknown> | null;

export interface ApiErrorDetails {
  [key: string]: unknown;
  current_class?: string[];
  next_class?: string[];
}

export class ApiError extends Error {
  status: number;
  code?: string;
  details?: ApiErrorDetails;

  constructor(
    message: string,
    status: number,
    code?: string,
    details?: ApiErrorDetails
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function request<T>(
  method: string,
  path: string,
  data?: unknown
): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...authHeaders(),
  };
  const tokenUsed = getAuthToken();

  const options: RequestInit = { method, headers, credentials: "same-origin" };
  if (data !== undefined && data !== null && method !== "GET") {
    options.body = JSON.stringify(data);
  }

  const response = await fetch(url, options);

  if (response.status === 204) return null as T;

  const json = await parseResponseBody(response);

  if (!response.ok) {
    if (response.status === 401) {
      if (getAuthToken() === tokenUsed) {
        clearAuth();
        window.location.href = getLoginHref();
      }
      throw new ApiError("Unauthorized", 401);
    }
    throw new ApiError(
      getResponseString(json, "error") || "Request failed",
      response.status,
      getResponseString(json, "code"),
      getResponseDetails(json)
    );
  }

  return json as T;
}

function getLoginHref(): string {
  if (typeof window === "undefined") return "/login";
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const redirect =
    current && current !== "/" && !current.startsWith("/login")
      ? `?redirect=${encodeURIComponent(current)}`
      : "";
  return `/login${redirect}`;
}

/** File routes are given either as "/api/..." or relative to the API base. */
function fileUrl(url: string): string {
  return url.startsWith("/api") ? url : `${BASE_URL}${url}`;
}

function clickDownload(href: string, filename: string): void {
  const a = document.createElement("a");
  a.href = href;
  a.download = filename || "download";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * Authorization headers for an authenticated request.
 *
 * Staff browsers are signed in by the `marine_session` cookie and hold no
 * bearer token. Only send the header when there is a token to put in it: the
 * API reads an unusable bearer as a rejected session and deletes the session
 * cookie, which signs the employee out of the browser.
 */
function authHeaders(): Record<string, string> {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function parseResponseBody(response: Response): Promise<ParsedResponseBody> {
  const text = await response.text();
  if (!text) return null;

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    try {
      return JSON.parse(text);
    } catch {
      if (response.ok) throw new Error("Server returned invalid JSON");
    }
  }

  try {
    return JSON.parse(text);
  } catch {
    return { error: text };
  }
}

function getResponseString(
  body: ParsedResponseBody,
  key: string
): string | undefined {
  const value = body?.[key];
  return typeof value === "string" ? value : undefined;
}

function getResponseDetails(
  body: ParsedResponseBody
): ApiErrorDetails | undefined {
  const value = body?.details;
  return value && typeof value === "object"
    ? (value as ApiErrorDetails)
    : undefined;
}

function buildQuery(params?: Record<string, unknown>): string {
  if (!params) return "";
  const filtered = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== ""
  );
  if (filtered.length === 0) return "";
  const qs = new URLSearchParams(
    filtered.map(([k, v]) => [k, String(v)])
  ).toString();
  return `?${qs}`;
}

export const api = {
  get: <T>(path: string, params?: Record<string, unknown>) =>
    request<T>("GET", `${path}${buildQuery(params)}`),

  post: <T>(path: string, data?: unknown) => request<T>("POST", path, data),

  put: <T>(path: string, data?: unknown) => request<T>("PUT", path, data),

  patch: <T>(path: string, data?: unknown) => request<T>("PATCH", path, data),

  delete: <T>(
    path: string,
    options?: { body?: unknown; query?: Record<string, unknown> }
  ) => {
    const fullPath = options?.query ? `${path}${buildQuery(options.query)}` : path;
    return request<T>("DELETE", fullPath, options?.body);
  },

  upload: async <T>(path: string, formData: FormData): Promise<T> => {
    const url = `${BASE_URL}${path}`;

    const response = await fetch(url, {
      method: "POST",
      headers: authHeaders(),
      body: formData,
      credentials: "same-origin",
    });
    const json = await parseResponseBody(response);
    if (!response.ok) {
      throw new ApiError(
        getResponseString(json, "error") || "Upload failed",
        response.status,
        getResponseString(json, "code")
      );
    }
    return json as T;
  },

  /**
   * Fetch a protected file with auth and return the blob itself, so the caller
   * decides how long its object URL stays alive. Use this for anything that
   * stays on screen; `fetchBlob` revokes its URL on a timer.
   */
  fetchFileBlob: async (url: string): Promise<Blob> => {
    const response = await fetch(fileUrl(url), {
      headers: authHeaders(),
      credentials: "same-origin",
    });
    if (!response.ok) throw new Error("Failed to fetch file");
    return response.blob();
  },

  /** POST a JSON body to a route that answers with a file, and return the blob. */
  postForBlob: async (path: string, data: unknown): Promise<Blob> => {
    const response = await fetch(`${BASE_URL}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify(data),
      credentials: "same-origin",
    });
    if (!response.ok) throw new Error("Failed to fetch file");
    return response.blob();
  },

  /** Fetch a protected file with auth and return a blob URL. */
  fetchBlob: async (url: string): Promise<string> => {
    const blob = await api.fetchFileBlob(url);
    const blobUrl = URL.createObjectURL(blob);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 300000);
    return blobUrl;
  },

  /** Fetch a protected file with auth and open in a new tab. */
  openFile: async (url: string): Promise<void> => {
    const blobUrl = await api.fetchBlob(url);
    window.open(blobUrl, "_blank");
  },

  /** Fetch a protected file with auth and trigger a download. */
  downloadFile: async (url: string, filename: string): Promise<void> => {
    clickDownload(await api.fetchBlob(url), filename);
  },

  /**
   * Download through the browser's own download manager, so large files stream
   * with progress instead of loading into memory first. Browser sessions
   * authenticate with the session cookie. The route must name the file in
   * Content-Disposition, since that name wins over `download`. A bearer-token
   * session (kiosk) has no cookie, so it falls back to downloadFile.
   */
  streamDownload: async (url: string, filename: string): Promise<void> => {
    if (getAuthToken()) return api.downloadFile(url, filename);
    clickDownload(fileUrl(url), filename);
  },
};
